import React from 'react';
import { ReportSection, ReportSubsection } from '../framework';
import { SectionProps } from './types';
import { Citation } from '../Citation';

export const RealEstateSection: React.FC<Pick<SectionProps, 'citations'>> = ({ citations }) => {
  return (
    <ReportSection id="realestate" title="Understanding Real Estate" subtitle="What does real estate actually return?">
      <p className="text-gray-700 mb-6">
        Before we look at any numbers, let's make sure we're thinking about real estate returns correctly.
        This matters because people often dramatically misunderstand how real estate makes (or loses) money.
      </p>

      <ReportSubsection title="The Three Ways Real Estate Makes Money">
        <p className="text-gray-700 mb-4">
          When you own a rental property, your returns come from three sources:
        </p>

        <div className="space-y-4 mb-6">
          <div className="bg-gray-50 p-4 border border-gray-200">
            <p className="font-semibold text-gray-900 mb-2">1. Price Appreciation</p>
            <p className="text-gray-600 text-sm">
              The house becomes worth more over time. This is what most people think about when they think
              about real estate returns. "I bought for $500K, now it's worth $800K." But appreciation alone
              is often overstated and needs to be adjusted for inflation.
            </p>
          </div>

          <div className="bg-gray-50 p-4 border border-gray-200">
            <p className="font-semibold text-gray-900 mb-2">2. Rental Income (Net of Expenses)</p>
            <p className="text-gray-600 text-sm">
              The cash flow from renting the property out—after subtracting property taxes, insurance,
              maintenance, management fees, and vacancy costs. For expensive coastal properties, this is
              often a smaller percentage of the property value than you'd expect.
            </p>
          </div>

          <div className="bg-gray-50 p-4 border border-gray-200">
            <p className="font-semibold text-gray-900 mb-2">3. Tax Benefits</p>
            <p className="text-gray-600 text-sm">
              Here's a weird thing about rental properties: the IRS lets you pretend they're losing value
              every year, even if they're actually appreciating. This is called "depreciation," and it's a
              paper loss that reduces your taxable income. For expensive properties, this can be worth tens
              of thousands of dollars per year in tax savings.
            </p>
          </div>
        </div>

        <p className="text-gray-700 mb-4">
          The total return is the combination of all three. When people tell you "real estate returns 10% per year,"
          ask them: are they including rental income? After expenses? Adjusted for inflation? Usually they're not.
        </p>
      </ReportSubsection>

      <ReportSubsection title="The Leverage Effect (Or Lack Thereof)">
        <p className="text-gray-700 mb-4">
          One thing that makes real estate returns confusing is leverage—borrowing money to buy property.
          If you put 20% down on a house and it appreciates 5%, your return on your down payment is actually
          25% (ignoring interest costs). This is why leveraged real estate can produce spectacular returns.
        </p>

        <p className="text-gray-700 mb-4">
          <strong>But this property is fully owned with no mortgage.</strong> That means we're looking at
          unlevered returns, which are much more comparable to stock market returns. No leverage means lower
          potential returns, but also lower risk—you can't lose the house to foreclosure if property values drop.
        </p>
      </ReportSubsection>

      <ReportSubsection title="Your House vs. 'The Market'">
        <p className="text-gray-700 mb-4">
          Here's something important that often gets overlooked: your specific house is not the same as
          "the real estate market." National indices like <Citation citation={citations.caseShillerNational}>
          Case-Shiller</Citation> track averages across thousands of properties. Your house might do better
          or worse depending on:
        </p>

        <ul className="list-disc list-inside text-gray-600 space-y-1 mb-4">
          <li>Neighborhood-specific changes (new development, school quality, crime)</li>
          <li>Property condition and maintenance</li>
          <li>Local supply and demand shifts</li>
          <li>Your specific tenant situation</li>
          <li>Random luck (the house next door sells for above asking, pulling up comps)</li>
        </ul>

        <p className="text-gray-700">
          This individual property risk is something you can diversify away with stocks (by owning 500 companies
          instead of one house), but not with a single rental property. Keep this in mind as we look at historical returns.
        </p>
      </ReportSubsection>
    </ReportSection>
  );
};
